import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for auto-saving form data to localStorage
 * @param {string} key - The localStorage key
 * @param {object} initialValue - Initial form values
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 1000)
 */
export const useAutoSave = (key, initialValue, debounceMs = 1000) => {
  const [value, setValue] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Check if saved data is not too old (24 hours)
        if (parsed._savedAt && Date.now() - parsed._savedAt < 24 * 60 * 60 * 1000) {
          const { _savedAt, ...data } = parsed;
          return { ...initialValue, ...data };
        }
      }
      return initialValue;
    } catch {
      return initialValue;
    }
  });

  const [isSaved, setIsSaved] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);

  // Debounced save to localStorage
  useEffect(() => {
    setIsSaved(false);
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({ ...value, _savedAt: Date.now() }));
        setIsSaved(true);
        setLastSaved(new Date());
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [key, value, debounceMs]);

  const updateValue = useCallback((updates) => {
    setValue(prev => {
      if (typeof updates === 'function') {
        return updates(prev);
      }
      return { ...prev, ...updates };
    });
  }, []);

  const clearSaved = useCallback(() => {
    localStorage.removeItem(key);
    setValue(initialValue);
  }, [key, initialValue]);

  const hasSavedData = useCallback(() => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed._savedAt && Date.now() - parsed._savedAt < 24 * 60 * 60 * 1000;
      }
      return false;
    } catch {
      return false;
    }
  }, [key]);

  return {
    value,
    setValue: updateValue,
    isSaved,
    lastSaved,
    clearSaved,
    hasSavedData
  };
};

/**
 * Hook for managing multi-step form progress
 */
export const useFormProgress = (totalSteps, initialStep = 1) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const progress = Math.round((completedSteps.size / totalSteps) * 100);

  const goToStep = (step) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const markComplete = (step) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  };

  const reset = () => {
    setCurrentStep(initialStep);
    setCompletedSteps(new Set());
  };

  return {
    currentStep,
    totalSteps,
    progress,
    completedSteps,
    goToStep,
    nextStep,
    prevStep,
    markComplete,
    reset,
    isFirst: currentStep === 1,
    isLast: currentStep === totalSteps
  };
};

/**
 * Hook for accessibility announcements
 */
export const useA11yAnnounce = () => {
  const announce = useCallback((message, priority = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }, []);

  return announce;
};
